export type CirclePrivacySettings = {
  momentumScore: boolean;
  workoutCompleted: boolean;
  workoutDetails: boolean;
  nutritionScore: boolean;
  calories: boolean;
  protein: boolean;
  recipes: boolean;
  sleep: boolean;
  water: boolean;
  streaks: boolean;
  badges: boolean;
};

export type CircleListItem = {
  id: string;
  name: string;
  description: string | null;
  inviteCode: string;
  memberCount: number;
  weeklyProgressPct: number;
  todayActivityCount: number;
  yourDailyScore: number;
};

export type CircleLimits = {
  circlesEnabled: boolean;
  memberCount: number;
  maxCircles: number | null;
  canJoinMultiple: boolean;
  canJoinMore: boolean;
};

export type CircleDashboardActivity = {
  id: string;
  circleId: string;
  circleName: string;
  type: string;
  message: string;
  createdAt: string;
  userName: string | null;
};

export type CircleDashboard = {
  enabled: boolean;
  limits: CircleLimits;
  circles: CircleListItem[];
  activities: CircleDashboardActivity[];
};

export type MemberDaySnapshot = {
  userId: string;
  name: string;
  dateKey: string;
  momentumScore: number | null;
  positiveSummary: string | null;
  streak: number | null;
  workouts: {
    id: string;
    name: string;
    activityType?: string;
    durationMinutes?: number | null;
    estimatedCaloriesBurned?: number | null;
  }[] | null;
  mealsLogged: number | null;
  calories: number | null;
  proteinG: number | null;
  carbsG: number | null;
  fatG: number | null;
  waterMl: number | null;
  sleepHours: number | null;
};

export type CircleInvitePreview = {
  id: string;
  name: string;
  description: string | null;
  memberCount: number;
  inviteCode: string;
};

export type LeaderboardEntry = {
  userId: string;
  name: string;
  dailyScore: number | null;
  streak: number | null;
  rank: number | null;
  rankDelta: number;
  highlight: string;
  role: string;
  isYou?: boolean;
};

export type CircleActivity = {
  id: string;
  type: string;
  message: string;
  createdAt: string;
  userName: string | null;
};

export type WorkoutPing = {
  id: string;
  senderId: string;
  senderName: string;
  workoutType: string;
  proposedTime: string | null;
  message: string | null;
  targetUserIds: string[];
  responses: Record<string, 'join' | 'maybe' | 'cant'>;
  createdAt: string;
};

export type SharedMeal = {
  id: string;
  title: string;
  calories: number;
  proteinG: number;
  carbsG: number;
  fatG: number;
  prepTime: number | null;
  rating: number | null;
  recipe: string | null;
  imageUrl: string | null;
  sharedBy: string;
  sharedById: string;
  createdAt: string;
};

export type CircleChallenge = {
  id: string;
  title: string;
  type: string;
  startDate: string;
  endDate: string;
  goals: { goalType?: string; target?: number };
  progress: Record<string, number>;
  participants: string[];
  status: string;
};

export type CircleBadge = {
  id: string;
  badgeType: string;
  title: string;
  description: string;
  earnedAt: string;
  userId: string;
};

export type CircleMember = {
  userId: string;
  name: string;
  role: string;
  joinedAt: string;
  dailyScore: number | null;
};

export type CircleDetail = {
  id: string;
  name: string;
  description: string | null;
  inviteCode: string;
  dateKey: string;
  isToday: boolean;
  circleProgressPct: number;
  privacy: CirclePrivacySettings;
  leaderboard: LeaderboardEntry[];
  activities: CircleActivity[];
  workoutPings: WorkoutPing[];
  sharedMeals: SharedMeal[];
  challenges: CircleChallenge[];
  members: CircleMember[];
  badges: CircleBadge[];
};

export const CHALLENGE_TYPES = [
  { id: 'protein_5', title: '5-Day Protein Challenge', type: 'protein', days: 5 },
  { id: 'workout_7', title: '7-Day Workout Streak', type: 'workout', days: 7 },
  { id: 'hydration', title: 'Hydration Week', type: 'hydration', days: 7 },
  { id: 'steps', title: 'Step Challenge', type: 'steps', days: 7 },
  { id: 'sleep', title: 'Sleep Consistency', type: 'sleep', days: 7 },
  { id: 'comeback', title: 'Comeback Week', type: 'comeback', days: 7 },
  { id: 'summer_cut', title: 'Summer Cut', type: 'cut', days: 30 },
  { id: 'bulk', title: 'Bulk Season', type: 'bulk', days: 30 },
  { id: '75hard', title: '75 Hard Style', type: '75hard', days: 75 },
] as const;

export const AI_MEAL_ADJUSTMENTS = [
  'Make lower calorie',
  'Make higher protein',
  'Make lower carb',
  'Double for meal prep',
  'Make dairy-free',
  'Fit into my remaining macros',
] as const;
